import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import HeroVideo from "@/components/HeroVideo";
import { HERO_VIDEOS } from "@/data/backgroundVideos";
import {
  Building2,
  Globe,
  Mail,
  Phone,
  User,
  Link as LinkIcon,
  PlusCircle,
  X,
  Loader2,
  CheckCircle,
  Award,
  Zap,
  ArrowRight,
  MapPin,
  Sparkles,
  Bot,
  LayoutDashboard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formSchema = z.object({
  name: z.string().min(2, "Business name must be at least 2 characters"),
  category_id: z.string().min(1, "Please select a category"),
  website: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
  address: z.string().min(5, "Please enter your business address"),
  description: z.string().min(50, "Description must be at least 50 characters"),
  logo_url: z.string().url("Please enter a valid image URL").optional().or(z.literal("")),
  affiliate_link: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
  contact_name: z.string().min(2, "Contact name is required"),
  contact_email: z.string().email("Please enter a valid email"),
  contact_phone: z.string().min(10, "Please enter a valid phone number"),
});

export default function SubmitBusinessPage() {
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();
  // Capture and persist the referrer slug from ?ref=… so it survives page
  // refreshes and the multi-step flow.
  const [referrerSlug] = useState(() => {
    const fromUrl = urlSearchParams.get("ref");
    if (fromUrl) {
      try { localStorage.setItem("lb_referrer_slug", fromUrl); } catch (e) { console.warn("referrer cache write failed:", e); }
      return fromUrl;
    }
    try { return localStorage.getItem("lb_referrer_slug") || ""; } catch (e) { console.warn("referrer cache read failed:", e); return ""; }
  });
  // OnlyBest Creator flow flag — set via /submit?kind=creator from the OnlyBest landing page.
  const isCreator = (urlSearchParams.get("kind") || "").toLowerCase() === "creator";
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [serviceInput, setServiceInput] = useState("");
  const [productInput, setProductInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submittedBusiness, setSubmittedBusiness] = useState(null);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      category_id: "",
      website: "",
      address: "",
      description: "",
      logo_url: "",
      affiliate_link: "",
      contact_name: "",
      contact_email: "",
      contact_phone: "",
    },
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/categories`);
        setCategories(res.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const addService = () => {
    if (serviceInput.trim() && !services.includes(serviceInput.trim())) {
      setServices([...services, serviceInput.trim()]);
      setServiceInput("");
    }
  };

  const removeService = (service) => {
    setServices(services.filter((s) => s !== service));
  };

  const addProduct = () => {
    if (productInput.trim() && !products.includes(productInput.trim())) {
      setProducts([...products, productInput.trim()]);
      setProductInput("");
    }
  };

  const removeProduct = (product) => {
    setProducts(products.filter((p) => p !== product));
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...data,
        services,
        products,
        referrer_slug: referrerSlug || undefined,
        listing_kind: isCreator ? "creator" : "business",
      };

      const res = await axios.post(`${API}/submit-business`, payload);
      try { localStorage.removeItem("lb_referrer_slug"); } catch (e) { console.warn("referrer cleanup failed:", e); }
      
      toast.success("Business submitted successfully!", {
        description: "Your listing is now live in the directory.",
      });
      
      // Show confirmation page instead of redirecting immediately
      setSubmittedBusiness(res.data);
      setShowConfirmation(true);
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to submit business", {
        description: error.response?.data?.detail || "Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Confirmation Page Component
  if (showConfirmation) {
    return (
      <div className="page-container min-h-screen" data-testid="listing-confirmation-page">
        <section className="py-20 md:py-32 hero-pattern relative overflow-hidden">
          <div className="hero-glow top-1/4 left-1/4 opacity-50" />
          <div className="hero-glow top-1/4 right-1/4 opacity-30" style={{ background: "radial-gradient(circle, rgba(16,185,129,0.3) 0%, transparent 70%)" }} />
          
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              {/* Success Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-24 h-24 mx-auto mb-8 rounded-full bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center"
              >
                <CheckCircle className="w-12 h-12 text-white" />
              </motion.div>
              
              <h1 className="text-3xl md:text-5xl font-bold font-['Outfit'] text-white mb-4">
                Your Free Business Listing Is Live 🎉
              </h1>
              
              <p className="text-[#94A3B8] text-lg max-w-xl mx-auto mb-8">
                Your business has been added to the LocalBest.ai Directory.
              </p>
              
              {/* What You Now Have */}
              <div className="glass-card rounded-2xl p-8 mb-8 text-left">
                <h3 className="text-white font-semibold mb-6 text-center">You now have:</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/20 flex items-center justify-center flex-shrink-0">
                      <Globe className="w-6 h-6 text-[#7C3AED]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">A free SEO-optimized business listing</p>
                      <p className="text-[#64748B] text-sm">Visible to thousands of potential customers</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#00E5FF]/20 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-6 h-6 text-[#00E5FF]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">An AI Business Hero ready to answer questions</p>
                      <p className="text-[#64748B] text-sm">24/7 customer support powered by AI</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#10B981]/20 flex items-center justify-center flex-shrink-0">
                      <LayoutDashboard className="w-6 h-6 text-[#10B981]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Access to your business dashboard</p>
                      <p className="text-[#64748B] text-sm">Manage your listing, track leads, and more</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Next Step */}
              <div className="bg-gradient-to-r from-[#7C3AED]/20 to-[#00E5FF]/20 border border-[#7C3AED]/30 rounded-xl p-6 mb-8">
                <p className="text-[#94A3B8] mb-4">
                  <span className="text-white font-medium">Next:</span> Activate your free trial to unlock full platform access, AI tools, retargeting, and automation.
                </p>
              </div>
              
              {/* CTA Buttons */}
              <div className="space-y-4">
                <Button
                  onClick={() => navigate("/pricing")}
                  className="w-full sm:w-auto px-8 py-6 text-lg bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] hover:opacity-90 transition-opacity"
                  data-testid="activate-trial-btn"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Activate My Free Trial
                </Button>
                
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/business/${submittedBusiness?.slug}`)}
                    className="border-white/20 text-white hover:bg-white/10"
                    data-testid="view-listing-btn"
                  >
                    View My Listing
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => navigate("/")}
                    className="text-[#94A3B8] hover:text-white"
                  >
                    Back to Directory
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-container min-h-screen" data-testid="submit-business-page">
      {/* Hero */}
      <section className="py-12 md:py-16 hero-pattern relative overflow-hidden">
        <div className="hero-glow top-1/4 left-1/4 opacity-50" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Hero Video — autoplay muted with unmute button */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <HeroVideo
                src={HERO_VIDEOS.ownTheNumberOne}
                header="Watch Video — Then Get Your Free Listing"
                testid="submit-hero-video"
              />
            </motion.div>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/10 border border-[#10B981]/20 mb-6">
              <Zap className="w-4 h-4 text-[#10B981]" />
              <span className="text-sm text-[#10B981]">{isCreator ? "Free OnlyBest Creator Listing" : "Free AI Business Listing"}</span>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold font-['Outfit'] text-white mb-4">
              {isCreator ? (
                <>Create My <span className="gradient-text">OnlyBest Creator</span> Account</>
              ) : (
                <>Create My <span className="gradient-text">Free Listing</span></>
              )}
            </h1>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto mb-4">
              {isCreator
                ? "Get your own LocalBest.ai creator listing — photo, bio, city, categories, recommendations, QR code, and shareable profile."
                : "Join the LocalBest.ai Directory and get your own AI Business Hero™ to help customers learn about your business 24/7."}
            </p>
            <p className="text-[#64748B] text-sm max-w-xl mx-auto">
              {isCreator
                ? "Includes Creator Page + Dashboard + Shareable QR Code"
                : "Includes AI Business Hero + Dashboard Access"}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Benefits */}
        <div className="grid grid-cols-3 gap-4 mb-12">
          {[
            { icon: Award, label: "SEO-Optimized Listing" },
            { icon: Zap, label: "AI Business Hero™" },
            { icon: LayoutDashboard, label: "Business Dashboard" },
          ].map((benefit, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="glass-card p-4 rounded-xl text-center"
            >
              <benefit.icon className="w-6 h-6 text-[#7C3AED] mx-auto mb-2" />
              <p className="text-white text-sm font-medium">{benefit.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-8 rounded-2xl"
        >
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Business Name */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">Business Name</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                        <Input
                          {...field}
                          placeholder="Your Business Name"
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                          data-testid="input-name"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Category */}
              <FormField
                control={form.control}
                name="category_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">Category</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger 
                          className="bg-white/5 border-white/10 text-white"
                          data-testid="select-category"
                        >
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-[#0a0f1a] border-white/10">
                        {categories.map((cat) => (
                          <SelectItem
                            key={cat.id}
                            value={cat.slug}
                            className="text-white hover:bg-white/10"
                          >
                            {cat.icon} {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Website */}
              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white flex items-center gap-2">
                      Website
                      <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] text-white">
                        Instantly Brands Your Listing
                      </span>
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                        <Input
                          {...field}
                          placeholder="https://yourbusiness.com"
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                          data-testid="input-website"
                        />
                      </div>
                    </FormControl>
                    <p className="text-xs text-[#94A3B8] mt-1.5 leading-relaxed">
                      Add your website and LocalBest.ai will automatically pull in your logo, brand colors, images, and business details to help build your Best of Best Listing faster.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Address */}
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">Business Address</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                        <Input
                          {...field}
                          placeholder="123 Main St, City, State ZIP"
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                          data-testid="input-address"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">Description</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Tell us about your business, services, and what makes you the best..."
                        className="min-h-[120px] bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                        data-testid="input-description"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Services */}
              <div>
                <label className="text-white text-sm font-medium block mb-2">
                  Services
                </label>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={serviceInput}
                    onChange={(e) => setServiceInput(e.target.value)}
                    placeholder="Add a service"
                    className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addService())}
                    data-testid="input-service"
                  />
                  <Button
                    type="button"
                    onClick={addService}
                    variant="outline"
                    className="border-white/10 text-white hover:bg-white/10"
                  >
                    <PlusCircle className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {services.map((service) => (
                    <span
                      key={service}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#7C3AED]/20 text-[#7C3AED] text-sm"
                    >
                      {service}
                      <button type="button" onClick={() => removeService(service)}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Products */}
              <div>
                <label className="text-white text-sm font-medium block mb-2">
                  Products
                </label>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={productInput}
                    onChange={(e) => setProductInput(e.target.value)}
                    placeholder="Add a product"
                    className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addProduct())}
                    data-testid="input-product"
                  />
                  <Button
                    type="button"
                    onClick={addProduct}
                    variant="outline"
                    className="border-white/10 text-white hover:bg-white/10"
                  >
                    <PlusCircle className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {products.map((product) => (
                    <span
                      key={product}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#00E5FF]/20 text-[#00E5FF] text-sm"
                    >
                      {product}
                      <button type="button" onClick={() => removeProduct(product)}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Logo URL */}
              <FormField
                control={form.control}
                name="logo_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">Logo URL (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="https://example.com/logo.png"
                        className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                        data-testid="input-logo"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Affiliate Link */}
              <FormField
                control={form.control}
                name="affiliate_link"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">Affiliate Link (Optional)</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                        <Input
                          {...field}
                          placeholder="https://yourbusiness.com?ref=affiliate"
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                          data-testid="input-affiliate"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Contact Section */}
              <div className="pt-4 border-t border-white/10">
                <h3 className="text-white font-semibold mb-4">Contact Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="contact_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white">Contact Name</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                            <Input
                              {...field}
                              placeholder="John Doe"
                              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                              data-testid="input-contact-name"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contact_email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white">Contact Email</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                            <Input
                              {...field}
                              placeholder="john@business.com"
                              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                              data-testid="input-contact-email"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="contact_phone"
                  render={({ field }) => (
                    <FormItem className="mt-4">
                      <FormLabel className="text-white">Contact Phone</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                          <Input
                            {...field}
                            placeholder="+1 (555) 123-4567"
                            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                            data-testid="input-contact-phone"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Submit Button */}
              <div className="space-y-3">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary py-6 text-lg"
                  data-testid="submit-business-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating Your Listing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Create My Free Listing
                    </>
                  )}
                </Button>
                <p className="text-center text-[#64748B] text-sm">
                  No credit card required. Upgrade anytime.
                </p>
              </div>
            </form>
          </Form>
        </motion.div>
      </div>
    </div>
  );
}
